import React, { createContext, useContext, useMemo } from "react";
import { PermissionError } from "../helpers/permissionErrors";
import {
  can,
  getEffectiveScope,
  getPermission,
  getVisibilityLevel,
  normalizeActor,
  resolveOrgPolicies,
} from "../helpers/permissions";

const PermissionContext = createContext(null);

export function PermissionProvider({
  actor,
  orgSettings,
  directReportIds,
  children,
}) {
  const value = useMemo(() => {
    const normalizedActor = normalizeActor(actor);
    const orgPolicies = resolveOrgPolicies(orgSettings);

    const buildContext = (context = {}) => ({
      ...context,
      directReportIds: context.directReportIds ?? directReportIds ?? [],
      orgSettings: context.orgSettings ?? orgSettings ?? {},
    });

    return {
      actor: normalizedActor,
      orgPolicies,
      directReportIds: directReportIds ?? [],
      can: (capability, context = {}) =>
        can(normalizedActor, capability, buildContext(context), orgPolicies),
      assertCan: (capability, context = {}) => {
        if (!can(normalizedActor, capability, buildContext(context), orgPolicies)) {
          throw new PermissionError(capability);
        }
      },
      getPermission: (capability, context = {}) =>
        getPermission(
          normalizedActor,
          capability,
          buildContext(context),
          orgPolicies,
        ),
      visibility: (capability, context = {}) =>
        getVisibilityLevel(
          normalizedActor,
          capability,
          buildContext(context),
          orgPolicies,
        ),
      scope: (capability, context = {}) =>
        getEffectiveScope(
          normalizedActor,
          capability,
          buildContext(context),
          orgPolicies,
        ),
      isHidden: (capability, context = {}) =>
        getVisibilityLevel(
          normalizedActor,
          capability,
          buildContext(context),
          orgPolicies,
        ) === "hidden",
      isReadOnly: (capability, context = {}) => {
        const level = getVisibilityLevel(
          normalizedActor,
          capability,
          buildContext(context),
          orgPolicies,
        );
        return level === "read" || level === "self";
      },
    };
  }, [actor, orgSettings, directReportIds]);

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissionContext() {
  return useContext(PermissionContext);
}
